import type { MailDomainView, ReceivingAddressRoute, TenantNeoMailboxView } from "@/api";
import type { StatusTone } from "@/ds";
import type { EmailWay } from "../router";
import { mailDomainCurrentStep, mailDomainTone } from "./screen-mail-domains.logic";

/**
 * Pure decisions behind Settings → Email (#1432): what each way enquiries reach a business says
 * about itself on its card. No React, no requests.
 *
 * Every input is a server read. In particular the address a reply leaves from is never worked out
 * here: the server computes it with the send path's own selection and this only passes it on.
 */

/** A read the section made itself. A failed read stays apart from an empty one. */
export type SectionRead<T> =
  | { readonly status: "loading" }
  | { readonly status: "failed" }
  | { readonly status: "ready"; readonly value: T };

export interface WayPill {
  readonly tone: StatusTone;
  readonly key: string;
  readonly vars?: Readonly<Record<string, string>>;
}

export interface WayCardState {
  /** Stable, for `data-state` and the tests. */
  readonly state: string;
  /** Null while the read is in flight: a pill that changes its mind a moment later is worse than none. */
  readonly pill: WayPill | null;
}

const LOADING: WayCardState = { state: "loading", pill: null };

/**
 * A read that failed says so and never "not set up". A business told it has no domain when we only
 * could not look would try to claim a second one, which is the rule the domain screen follows too.
 */
const UNAVAILABLE: WayCardState = {
  state: "unavailable",
  pill: { tone: "neutral", key: "settingsEmail.unavailable" },
};

export function addressCardState(read: SectionRead<TenantNeoMailboxView>): WayCardState {
  if (read.status === "loading") return LOADING;
  if (read.status === "failed") return UNAVAILABLE;
  return read.value.emailAddress
    ? { state: "active", pill: { tone: "success", key: "settingsEmail.address.active" } }
    : { state: "missing", pill: { tone: "warning", key: "settingsEmail.address.missing" } };
}

export function forwardingCardState(read: SectionRead<number>): WayCardState {
  if (read.status === "loading") return LOADING;
  if (read.status === "failed") return UNAVAILABLE;
  const count = read.value;
  if (count === 0) return { state: "none", pill: { tone: "neutral", key: "settingsEmail.forwarding.none" } };
  return {
    state: "some",
    pill: count === 1
      ? { tone: "success", key: "settingsEmail.forwarding.one" }
      : { tone: "success", key: "settingsEmail.forwarding.many", vars: { count: String(count) } },
  };
}

/** A business holds at most one domain (#1430), so the first row is the one there is. */
export function heldDomain(read: SectionRead<readonly MailDomainView[]>): MailDomainView | null {
  return read.status === "ready" ? read.value[0] ?? null : null;
}

export function domainCardState(read: SectionRead<readonly MailDomainView[]>): WayCardState {
  if (read.status === "loading") return LOADING;
  if (read.status === "failed") return UNAVAILABLE;
  const row = heldDomain(read);
  if (!row) return { state: "none", pill: { tone: "neutral", key: "settingsEmail.notSetUp" } };
  // Ready exactly where the domain's own panel folds to its summary, so the card and the panel
  // below it cannot disagree about whether anything is left to do.
  if (mailDomainCurrentStep(row) === "done") {
    return { state: "ready", pill: { tone: "success", key: "mailDomain.ready" } };
  }
  return { state: row.state, pill: { tone: mailDomainTone(row.state), key: `mailDomain.state.${row.state}` } };
}

/** The addresses of mailboxes the business brought itself, as the server lists them. */
export function ownMailboxAddresses(read: SectionRead<TenantNeoMailboxView>): string[] {
  if (read.status !== "ready") return [];
  return (read.value.receiving ?? [])
    .filter((route) => route.kind === "ownMailbox")
    .map((route) => route.address);
}

/**
 * Connected or not set up, and nothing about health: the server keeps no record of whether a
 * mailbox still answers, so a "failing" state would have nothing true to stand on (#1432).
 */
export function mailboxCardState(read: SectionRead<TenantNeoMailboxView>): WayCardState {
  if (read.status === "loading") return LOADING;
  // A server that does not list the addresses cannot say whether one is connected.
  if (read.status === "failed" || !read.value.receiving) return UNAVAILABLE;
  return ownMailboxAddresses(read).length > 0
    ? { state: "connected", pill: { tone: "success", key: "settingsEmail.mailbox.connected" } }
    : { state: "none", pill: { tone: "neutral", key: "settingsEmail.notSetUp" } };
}

/** Where replies to each receiving address leave from: the server's list, as it came. */
export function replyRoutes(read: SectionRead<TenantNeoMailboxView>): readonly ReceivingAddressRoute[] | null {
  if (read.status !== "ready" || !read.value.receiving) return null;
  return read.value.receiving;
}

export type EmailWayAction = "howTo" | "manage" | "setUp" | "details";

/**
 * The words on a card's one button. Only the platform owner can change a mailbox, so anyone else is
 * offered details rather than a setup the server would refuse them.
 */
export function wayAction(way: EmailWay, card: WayCardState, canEditMailbox: boolean): EmailWayAction {
  switch (way) {
    case "address":
      return "howTo";
    case "forwarding":
      return "manage";
    case "domain":
      return card.state === "none" ? "setUp" : "manage";
    case "mailbox":
      if (!canEditMailbox) return "details";
      return card.state === "connected" ? "manage" : "setUp";
  }
}
